"use client";

import ExternalLinkIcon from "@/assets/ExternalLinkIcon";
// [RUDDERSTACK] widget_impression + cta_click are added centrally by NewPage
// (TrackedWidget + CtaClickDelegate). The CTA below carries data-cta-name so the
// delegate keeps the exact "apply_now" name; no tracking code lives here.
import { hexToRgba } from "@/utils/utils";
import cn from "classnames";
import Image from "next/image";
import { useMemo, useRef } from "react";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { useDispatch } from "react-redux";

interface KeyHighlight {
  id: string;
  icon?: string;
  icon_alt?: string;
  heading?: string;
  subtext?: string;
  show_divider?: boolean;
  divider_color?: string;
  priority?: number;
  status?: boolean;
}

interface BulletPoint {
  id: string;
  text?: string;
  icon?: string;
  icon_alt?: string;
  font_weight?: string;
  font_color?: string;
  priority?: number;
  status?: boolean;
}

interface AboutData {
  id: number;
  page_id: number;
  background_color?: string;
  title?: string;
  title_text_color?: string;
  description?: string;
  description_text_color?: string;
  sub_text?: string;
  sub_text_color?: string;
  highlight_border_color?: string;
  desktop_image?: string;
  mobile_image?: string;
  alt_image?: string;
  cta_label?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  cta_text_color?: string;
  cta_bg_color?: string;
  cta_enable_shadow?: boolean;
  show_image?: boolean;
  show_key_highlights?: boolean;
  key_highlight_heading_color?: string;
  key_highlight_subtext_color?: string;
  key_highlight_divider_color?: string;
  metadata?: KeyHighlight[];
  metadata_1?: BulletPoint[];
  status?: boolean;
}

interface AboutProps {
  data: AboutData;
}

const MAROON = "#9F1C34";
const BLACK = "#101112";
const DIVIDER = "rgba(0,0,0,0.1)";
const MAX_HIGHLIGHTS = 6;
const MAX_BULLETS = 4;

const toBool = (v: unknown, fallback = true) =>
  v === undefined || v === null ? fallback : Boolean(v);

const About3 = ({ data }: { data: AboutProps }) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);

  const {
    background_color = "#FFFFFF",
    title = "",
    title_text_color = BLACK,
    description = "",
    description_text_color = BLACK,
    sub_text = "",
    sub_text_color = BLACK,
    highlight_border_color = MAROON,
    desktop_image = "",
    mobile_image = "",
    alt_image = "",
    cta_label = "Apply Now",
    cta_icon = "",
    cta_icon_alt = "",
    cta_text_color = "#FFFFFF",
    cta_bg_color = MAROON,
    cta_enable_shadow,
    show_image,
    show_key_highlights,
    key_highlight_heading_color = MAROON,
    key_highlight_subtext_color = BLACK,
    key_highlight_divider_color = DIVIDER,
    metadata = [],
    metadata_1 = [],
  } = data?.data || {};

  // No cross-breakpoint fallback: the mobile image shows only if a mobile image
  // was uploaded, the desktop image only if a desktop image was uploaded. Each
  // viewport renders its own upload or nothing — they never borrow the other's.
  const respectsShowImage = toBool(show_image, true);
  const showMobileImage = respectsShowImage && Boolean(mobile_image);
  const showDesktopImage = respectsShowImage && Boolean(desktop_image);
  // Render the image container when either viewport has an image to show.
  const showImage = showMobileImage || showDesktopImage;
  const showKeyHighlights = toBool(show_key_highlights, true);
  const enableShadow = toBool(cta_enable_shadow, true);

  // Key-highlight columns: 2 per row when the DESKTOP image narrows the text
  // column, 3 per row when the text spans full width (no desktop image). Driven
  // by the desktop image specifically, since that's what occupies the side column.
  const highlightCols = showDesktopImage ? "sm:grid-cols-2" : "sm:grid-cols-3";

  // Active key highlights, ordered by priority, capped at 6 (PRD). Memoized so
  // the transform is not re-run on every render (above-the-fold widget).
  const highlights = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item) => toBool(item?.status, true))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0))
      .slice(0, MAX_HIGHLIGHTS);
  }, [metadata]);

  // Bullet points (metadata_1): active only, ordered by priority, capped at 4.
  // A bullet renders only if it has text; each carries its own font weight,
  // colour and optional icon. No line-clamp (full text always shown).
  const bullets = useMemo(() => {
    if (!metadata_1 || metadata_1.length === 0) return [];
    return [...metadata_1]
      .filter((item) => toBool(item?.status, true) && Boolean(item?.text))
      .sort((a, b) => (a.priority || 0) - (b.priority || 0))
      .slice(0, MAX_BULLETS);
  }, [metadata_1]);

  if (!data?.data?.status) return null;

  // Block gap (heading→content→stats→cta): 24 · 30 · 36 · 40 · 40
  const blockGap = "gap-6 sm:gap-[30px] md:gap-9 lg:gap-10";

  return (
    <section
      ref={ref}
      style={{ backgroundColor: background_color }}
      className={cn(
        "w-full",
        "px-6 py-[30px] sm:px-10 md:px-[60px] md:py-10 lg:px-20 lg:py-[60px] xl:px-40",
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-6",
          "sm:grid sm:items-stretch",
          // Two columns (text + side image) only when a desktop image is shown;
          // otherwise a single full-width column so the text doesn't leave an
          // empty side gap on desktop.
          showDesktopImage
            ? "sm:grid-cols-[1fr_auto] sm:gap-x-[30px] md:gap-x-[60px] lg:gap-x-[73px]"
            : "sm:grid-cols-1",
          "sm:gap-y-[30px] md:gap-y-9 lg:gap-y-10",
        )}
      >
        {/* Intro — heading + paragraph + highlight callout */}
        <div
          className={cn(
            "flex flex-col sm:col-start-1 sm:row-start-1",
            blockGap,
          )}
        >
          {title && (
            <h2
              className={cn(
                "line-clamp-none sm:line-clamp-2 text-center font-semibold sm:text-left",
                "leading-[1.25] xl:leading-[1.2]",
                "tracking-[-0.42px] md:tracking-[-0.48px] lg:tracking-[-0.54px] xl:tracking-[-0.63px]",
                "text-[28px] md:text-[32px] lg:text-[36px] xl:text-[42px]",
              )}
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
          )}

          {(description || sub_text || bullets.length > 0) && (
            <div className="flex flex-col gap-6">
              {description && (
                <p
                  className={cn(
                    // DEG-8893: cap the description at 6 lines (ellipsis) on
                    // desktop so long content can't grow the widget height / push
                    // the image. Mobile wraps fully — no truncation.
                    "line-clamp-none sm:line-clamp-6",
                    "text-center sm:text-left",
                    "leading-[1.65] sm:leading-[1.6] md:leading-[1.65]",
                    "font-medium sm:font-normal lg:font-medium xl:font-normal",
                    "text-[14px] sm:text-[16px] lg:text-[17px] xl:text-[18px]",
                  )}
                  style={{ color: description_text_color }}
                >
                  {description}
                </p>
              )}

              {bullets.length > 0 && (
                <div className="flex flex-col gap-[14px] mb-2">
                  {bullets.map((bullet) => (
                    <div key={bullet.id} className="flex gap-[14px]">
                      {bullet.icon && (
                        <Image
                          src={bullet.icon}
                          alt={bullet.icon_alt || ""}
                          width={24}
                          height={24}
                          className="size-[24px] shrink-0 object-contain md:size-6 mt-1"
                        />
                      )}
                      <p
                        className={cn(
                          "leading-[1.65] sm:leading-[1.6] md:leading-[1.65]",
                          "text-[14px] sm:text-[16px] lg:text-[17px] xl:text-[18px]",
                        )}
                        style={{
                          color: bullet.font_color || description_text_color,
                          fontWeight: Number(bullet.font_weight) || 500,
                        }}
                      >
                        {bullet.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {sub_text && (
                <div
                  className="flex items-center rounded-[10px] border-l-4 border-solid px-4 py-2"
                  style={{ borderColor: highlight_border_color }}
                >
                  <p
                    className={cn(
                      // Mobile (DEG-8894): show full highlighted text, wrapping
                      // freely with no ellipsis. Keep the 3-line clamp on desktop.
                      "line-clamp-none sm:line-clamp-3 font-semibold xl:font-medium",
                      "leading-[1.5] xl:leading-[1.4]",
                      "text-[16px] lg:text-[18px] xl:text-[20px]",
                    )}
                    style={{ color: sub_text_color }}
                  >
                    {sub_text}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Side image — middle on mobile, right column on desktop. Fixed sizes
            per breakpoint: 327×398 · 390×482 (1080/1230) · 477×591 · 502×620.
            Each viewport shows ONLY its own upload (no cross-breakpoint fallback):
            the container is visible on mobile only when a mobile image exists, and
            on desktop only when a desktop image exists. */}
        {showImage && (
          <div
            className={cn(
              // self-center keeps the image horizontally centered on mobile;
              // sm:self-start top-aligns it on desktop so it no longer drifts
              // down as the text column grows (DEG-8892).
              "relative max-w-full self-center overflow-hidden",
              // Per-viewport visibility — no borrowing the other viewport's image.
              showMobileImage ? "block" : "hidden",
              showDesktopImage ? "sm:block" : "sm:hidden",
              "h-[398px] w-[327px]",
              "sm:col-start-2 sm:row-start-1 sm:row-span-2 sm:self-start sm:h-[482px] sm:w-[390px]",
              "lg:h-[591px] lg:w-[477px]",
              "xl:h-[620px] xl:w-[502px]",
            )}
          >
            {/* Mobile (< 1080) — only when a mobile image was uploaded */}
            {showMobileImage && (
              <Image
                src={mobile_image}
                alt={alt_image || "About"}
                fill
                sizes="327px"
                className="object-cover sm:hidden"
              />
            )}
            {/* Desktop (>= 1080) — only when a desktop image was uploaded */}
            {showDesktopImage && (
              <Image
                src={desktop_image}
                alt={alt_image || "About"}
                fill
                sizes="(min-width: 1750px) 502px, (min-width: 1430px) 477px, 390px"
                className="hidden object-cover sm:block"
              />
            )}
          </div>
        )}

        {/* Key highlights + CTA */}
        <div
          className={cn(
            "flex flex-col sm:col-start-1 sm:row-start-2",
            blockGap,
          )}
        >
          {showKeyHighlights && highlights.length > 0 && (
            // 2 cols (with image) / 3 cols (no image) on desktop; stacked on
            // mobile. Grid keeps lone wrapped items at their column width.
            <div
              className={cn(
                "grid grid-cols-1 gap-y-6 sm:gap-x-10 sm:gap-y-8",
                highlightCols,
              )}
            >
              {highlights.map((item) => (
                <div key={item.id} className="flex min-w-0 flex-col gap-2">
                  <div className="flex items-center gap-3">
                    {item.icon && (
                      <Image
                        src={item.icon}
                        alt={item.icon_alt || ""}
                        width={24}
                        height={24}
                        className="size-[22px] shrink-0 object-contain md:size-6"
                      />
                    )}
                    {item.heading && (
                      <p
                        className={cn(
                          "font-semibold leading-[1.4] xl:leading-[1.35]",
                          "tracking-[-0.09px] md:tracking-[-0.1px] lg:tracking-[-0.11px] xl:tracking-[-0.12px]",
                          "text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px]",
                        )}
                        style={{ color: key_highlight_heading_color }}
                      >
                        {item.heading}
                      </p>
                    )}
                  </div>

                  {toBool(item.show_divider, true) && (
                    <div
                      className="h-px w-full"
                      style={{
                        // Per-row colour wins; otherwise the block-level default.
                        backgroundColor:
                          item.divider_color || key_highlight_divider_color,
                      }}
                    />
                  )}

                  {item.subtext && (
                    <p
                      className={cn(
                        "line-clamp-2 leading-[1.55]",
                        "tracking-[0.06px] sm:tracking-[0.07px] lg:tracking-[0.075px] xl:tracking-[0.08px]",
                        "text-[12px] sm:text-[14px] lg:text-[15px] xl:text-[16px]",
                      )}
                      style={{ color: key_highlight_subtext_color }}
                    >
                      {item.subtext}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {cta_label && (
            // Apply Now — opens the lead drawer. cta_click fires via the page
            // CtaClickDelegate using data-cta-name below.
            <button
              type="button"
              data-cta-name="apply_now"
              style={{
                backgroundColor: cta_bg_color,
                color: cta_text_color,
                boxShadow: enableShadow
                  ? `0px 10px 20px ${hexToRgba(cta_bg_color, 0.45)}`
                  : undefined,
              }}
              className={cn(
                "flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-[8px]",
                "px-6 py-4 sm:w-auto sm:self-start sm:px-5 sm:py-3 lg:px-6 lg:py-4",
              )}
              onClick={() => {
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  }),
                );
              }}
            >
              <span className="whitespace-nowrap font-semibold leading-[1.4] text-[16px] sm:text-[14px] sm:font-medium md:font-semibold lg:text-[16px]">
                {cta_label}
              </span>
              {cta_icon ? (
                <Image
                  src={cta_icon}
                  alt={cta_icon_alt || ""}
                  width={24}
                  height={24}
                  className="size-6 shrink-0 object-contain"
                />
              ) : (
                // Default export arrow; inherits the button's text color.
                <ExternalLinkIcon className="size-6 shrink-0" />
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default About3;
